import type { Item, Layer, Part, Section } from '../../domain';
import { cmToMeters } from '../../domain';
import { boxTransform, makeNode, translate, type SceneNode, type Transform } from '../SceneGraph';
import { BaseSystemBuilder } from './BaseSystemBuilder';

const HIDE_PANEL_BOX = 0.08; // бленда (panel), м
const TUBE_OFFSET = 0.02; // подъём центра вала (Ø4см) над верхом полотна, м
const TUBE_R = 0.02; // радиус вала, м
// L-кронштейн (стена/потолок) по спеку: высота 50мм (=Ø торцевого колпака),
// вылет 62мм (короткий вариант), пластина/рёбра ~4мм.
const BRACKET_H = 0.05;
const BRACKET_ARM = 0.062;
const BRACKET_TH = 0.004;
// Кронштейн стоит снаружи вала: между его торцом и креплением зажаты шестерня/
// проставка цепи (~25мм). Сплошной скруглённый торец плеча выносится чуть за них
// (25мм + зазор), чтобы накрывать торец вала/шестерни без z-fighting и просветов.
const BRACKET_OUT = 0.03;
// Полотно свисает с ДАЛЬНЕЙ (пристенной) кромки вала, оставаясь намотанным
// хотя бы на оборот — плоскость полотна смещена назад к задней кромке вала.
const FABRIC_Z = -(TUBE_R - 0.003);

/** Высота полотна (сумма секций) в метрах — верх полотна, к нему крепятся вал/панель. */
function fabricTopM(layer: Layer): number {
  return cmToMeters(layer.sections.reduce((acc, s) => acc + s.height, 0));
}

/**
 * Билдер рулонной шторы (rollo). Все детали — реальные модели старого проекта,
 * как в TypeRollo.cs: вал `rollo_n_top` (Ø4см), торцы/крепления `rollo_n_top_end`
 * (ориентация по binding), рейки `baserail`, нижний утяжелитель, мотор/шнур.
 * Без примитивов/«дорисовки». Всё позиционируется по реальной высоте полотна.
 * Splitt — общий каркас. Крепления динамичны по выбору пользователя (binding).
 */
export class RolloBuilder extends BaseSystemBuilder {
  protected override buildItemExtras(item: Item): SceneNode[] {
    const out: SceneNode[] = [];
    const layer0 = item.parts[0]?.layers[0];
    const topY = layer0 ? fabricTopM(layer0) : cmToMeters(item.height);
    const w = cmToMeters(item.width);

    // Крепления вала. Зависят от binding (динамика по выбору пользователя):
    // aperture — нишевое крепление `apperturebind`; стена/потолок — L-кронштейн
    // (один и тот же, ceiling — поворотом X). Старый круглый торец `rollo_n_top_end`
    // как крепление больше не ставим — его заменяет L-кронштейн.
    const binding = item.binding ?? 'wall';
    const ecY = topY + TUBE_OFFSET;
    const rotX = binding === 'ceiling' ? Math.PI / 2 : 0;
    // Для hidePanel=mounting крепления заменяет кассета (как в коде).
    if (item.hidePanel !== 'mounting') {
      for (const sx of [-1, 1] as const) {
        const side = sx < 0 ? 'left' : 'right';
        if (binding === 'aperture') {
          out.push(
            makeNode({
              id: `mount.${sx < 0 ? 'l' : 'r'}`,
              meshId: 'rollo/apperturebind',
              materialId: item.pcFixedColor ?? undefined,
              transform: {
                position: [sx * (w / 2), ecY, 0],
                rotation: [rotX, 0, sx < 0 ? 0 : Math.PI],
                scale: [1, 1, 1],
              },
              tags: ['mount', binding, side],
            }),
          );
        } else {
          out.push(
            makeNode({
              id: `mount.${sx < 0 ? 'l' : 'r'}`,
              meshId: 'proc/wallBracket', // не резолвится → процедурная форма ниже
              materialId: item.pcFixedColor ?? undefined,
              transform: {
                // снаружи вала: шестерня/проставка (~25мм) зажаты между торцом и креплением
                position: [sx * (w / 2 + BRACKET_OUT), ecY, 0],
                // канон = правый борт (пластина внутрь −X); левый — зеркало Z=π
                rotation: [rotX, 0, sx < 0 ? Math.PI : 0],
                scale: [BRACKET_TH, BRACKET_H, BRACKET_ARM], // [толщина, высота, вылет]
              },
              shape: 'wallBracket',
              tags: ['mount', 'bracket', binding, side],
            }),
          );
        }
      }
    }

    // Верхняя панель (кассета/бленда/профиль) — реальная сборка.
    if (item.hidePanel != null) out.push(...this.buildHidePanel(item, topY, w));
    return out;
  }

  /** Сборка верхней панели из реальных мешей (как в TypeRollo.cs). */
  private buildHidePanel(item: Item, topY: number, w: number): SceneNode[] {
    const hp = item.hidePanel;
    if (hp == null) return [];
    const mat = item.ptColor ?? undefined;
    const y = topY + 0.03;

    // panel → бленда (одна панель спереди)
    if (hp === 'panel') {
      return [
        makeNode({
          id: 'hidepanel',
          meshId: 'rollo/blende',
          materialId: mat,
          transform: boxTransform([0, y, 0.04], [w + 0.1, HIDE_PANEL_BOX, HIDE_PANEL_BOX]),
          fit: { mode: 'stretch', axis: 'x', length: w + 0.1 },
          tags: ['hidepanel', 'panel'],
        }),
      ];
    }

    // cartridge / mounting → перёд + два торца (+ верх для ceiling у cartridge)
    const sideMesh = hp === 'mounting' ? 'rollo/hp_mountside' : 'rollo/hp_side';
    const nodes: SceneNode[] = [
      makeNode({
        id: 'hidepanel',
        meshId: 'rollo/hp_front',
        materialId: mat,
        // перёд авторен «плоско» (yup свопнул оси) → поворот X 90° делает его вертикальным
        transform: { position: [0, y, 0.045], rotation: [Math.PI / 2, 0, 0], scale: [1, 1, 1] },
        fit: { mode: 'stretch', axis: 'x', length: w },
        tags: ['hidepanel', hp],
      }),
    ];
    for (const sx of [-1, 1] as const) {
      nodes.push(
        makeNode({
          id: `hidepanel.side.${sx < 0 ? 'l' : 'r'}`,
          meshId: sideMesh,
          materialId: mat,
          transform: {
            position: [sx * (w / 2), y, 0.045],
            rotation: [0, 0, sx < 0 ? Math.PI : 0],
            scale: [1, 1, 1],
          },
          tags: ['hidepanel', hp, 'side'],
        }),
      );
    }
    if (item.binding === 'ceiling' && hp !== 'mounting') {
      nodes.push(
        makeNode({
          id: 'hidepanel.top',
          meshId: 'rollo/hp_top',
          materialId: mat,
          transform: {
            position: [0, y + 0.05, 0],
            rotation: [Math.PI / 2, 0, 0],
            scale: [1, 1, 1],
          },
          fit: { mode: 'stretch', axis: 'x', length: w },
          tags: ['hidepanel', hp, 'top'],
        }),
      );
    }
    return nodes;
  }

  protected override buildTop(
    item: Item,
    part: Part,
    layer: Layer,
    iP: number,
    iL: number,
  ): SceneNode {
    const w = cmToMeters(part.width);
    const y = fabricTopM(layer) + TUBE_OFFSET;
    // Вал `rollo_n_top` (Ø4см) нормирован длинной осью X → тянем по X до ширины,
    // диаметр — натуральный.
    return makeNode({
      id: `top.${iP}.${iL}`,
      meshId: 'rollo/top',
      materialId: item.pcFixedColor ?? undefined,
      transform: boxTransform([0, y, 0], [w, 0.2, 0.2]),
      fit: { mode: 'stretch', axis: 'x', length: w },
      tags: ['top'],
    });
  }

  /** Рейки: нижняя граница — реальный утяжелитель, промежуточные — реальные рейки. */
  protected override buildRail(
    layer: Layer,
    iP: number,
    iL: number,
    i: number,
    y: number,
  ): SceneNode {
    const w = cmToMeters(layer.width);
    const isBottom = i === 0;
    // Промежуточная рейка `rollo_n_rail` лежит плоско вдоль полотна: поворот 90°
    // по X кладёт её тонкой стороной перпендикулярно (мин. выступ), высота — в Y.
    const transform: Transform = {
      position: [0, y, FABRIC_Z],
      rotation: isBottom ? [0, 0, 0] : [Math.PI / 2, 0, 0],
      scale: isBottom ? [w, 0.03, 0.0025] : [w, 0.02, 0.02],
    };
    return makeNode({
      id: `rail.${iP}.${iL}.${i}`,
      meshId: isBottom ? 'rollo/bottombar' : 'rail',
      materialId: layer.railColor ?? undefined,
      transform,
      // Обычная рейка после поворота: высота 10 мм, толщина 2,5 мм.
      fit: isBottom
        ? { mode: 'stretch', axis: 'x', length: w, crossSection: [0.03, 0.0025] }
        : { mode: 'stretch', axis: 'x', length: w },
      tags: isBottom ? ['rail', 'bottombar'] : ['rail'],
    });
  }

  /** Секция полотна — смещена к пристенной кромке вала (FABRIC_Z). */
  protected override buildSection(
    _layer: Layer,
    section: Section,
    iP: number,
    iL: number,
    i: number,
    yCenter: number,
  ): SceneNode {
    const w = cmToMeters(section.width);
    const hh = cmToMeters(section.height);
    return makeNode({
      id: `sec.${iP}.${iL}.${i}`,
      meshId: 'section',
      materialId: section.materialColor ?? undefined,
      transform: boxTransform([0, yCenter, FABRIC_Z], [w, hh, 0.0015]),
      uvWorld: true, // текстура полотна мостится фикс. масштабом, без растяжения
      tags: ['section'],
    });
  }

  protected override buildControl(_item: Item, layer: Layer, iP: number, iL: number): SceneNode {
    const side = layer.controlSide ?? 'right';
    const w = cmToMeters(layer.width);
    const h = fabricTopM(layer); // высота полотна

    if (layer.controlType === 'manual') {
      // Кольцевая шариковая цепь через шестерню, зажатую между торцом вала и
      // креплением; с другого торца — проставка той же толщины. Цепь свисает
      // замкнутой петлёй (две нити шариков Ø4мм + chain guidance снизу).
      const sx = side === 'left' ? -1 : 1;
      // Диск от торца вала почти до фланца, оставляя ~1мм зазор у крепления
      // (шестерня вращается, крепление неподвижно); к валу — плотно.
      const discT = 0.025; // толщина диска, м (на ~1мм меньше промежутка вал↔фланец)
      const discDia = 0.04; // диаметр диска = диаметр вала
      const sprocketX = sx * (w / 2 + discT / 2); // внутренняя грань — у торца вала
      const spacerX = -sprocketX; // проставка с другого торца
      const tubeY = h + TUBE_OFFSET;
      const loopR = 0.012; // полуширина петли (нить перёд/зад ± loopR по Z)
      const len = h * 0.7; // длина свисающей части
      const strandCenter = tubeY - len / 2;
      const chainMat = layer.chainColor ?? undefined;
      // Шестерня, обойма и проставка — нейлон (пластик), отдельные от алюм. вала.
      const disc = (id: string, x: number, tag: string): SceneNode =>
        makeNode({
          id,
          meshId: `chain/${tag}`,
          materialId: 'NYLON',
          transform: boxTransform([x, tubeY, 0], [discT, discDia, discDia]),
          shape: 'cylinderX',
          tags: ['chain', tag, side],
        });
      const strand = (id: string, z: number): SceneNode =>
        makeNode({
          id,
          meshId: 'chain/bead',
          materialId: chainMat,
          transform: boxTransform([sprocketX, strandCenter, z], [0.004, len, 0.004]),
          shape: 'beadChain',
          tags: ['chain', 'control', side],
        });
      return makeNode({
        id: `chain.${iP}.${iL}`,
        transform: translate([0, 0, 0]),
        tags: ['chain', 'control', side],
        children: [
          disc(`chain.sprocket.${iP}.${iL}`, sprocketX, 'sprocket'), // шестерня у торца
          disc(`chain.spacer.${iP}.${iL}`, spacerX, 'spacer'), // проставка с другого торца
          strand(`chain.front.${iP}.${iL}`, loopR),
          strand(`chain.back.${iP}.${iL}`, -loopR),
          // низ петли — «chain guidance» (тензор ~45мм), через который проходит цепь
          makeNode({
            id: `chain.guide.${iP}.${iL}`,
            meshId: 'chain/guide',
            materialId: 'NYLON',
            transform: boxTransform([sprocketX, tubeY - len - 0.02, 0], [0.012, 0.045, 2 * loopR]),
            tags: ['chain', 'chainguide', side],
          }),
        ],
      });
    }
    // engine / battery / network → мотор-вставка в торец вала со стороны управления
    // (в реальности внутри вала; голова слегка выступает у торца).
    const k = side === 'left' ? -1 : 1;
    return makeNode({
      id: `motor.${iP}.${iL}`,
      meshId: 'motor',
      transform: translate([k * (w / 2 - 0.02), h + TUBE_OFFSET, 0]),
      tags: ['motor', 'control', side],
    });
  }
}
